import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { BillingUiStateKind } from '@this+that/models';
import { getBillingUiState } from '@this+that/utils';

import {
  BILLING_GATE_EXEMPT_PATH_PREFIXES,
  IS_BILLING_GATE_ENABLED,
} from '../../pages/private/Billing/constants';
import { APP_PAGES } from '../../routes/constants';
import { useActiveTeamId, useTeamStore } from '../../store/Team/useTeamStore';
import { isSafeInternalPath } from '../../utils/postOAuthReturn';

interface ShouldRedirectToBillingParams {
  enabled: boolean;
  uiStateKind: BillingUiStateKind | undefined;
  pathname: string;
}

export function shouldRedirectToBilling({
  enabled,
  uiStateKind,
  pathname,
}: ShouldRedirectToBillingParams): boolean {
  if (!enabled) {
    return false;
  }
  if (uiStateKind !== BillingUiStateKind.APP_TRIAL_EXPIRED) {
    return false;
  }
  const isExempt = [...BILLING_GATE_EXEMPT_PATH_PREFIXES].some((prefix) =>
    pathname.startsWith(prefix),
  );
  return !isExempt;
}

interface TrialGateLocationState {
  gatedFrom?: string;
}

interface ResolveTrialGateReturnPathParams {
  uiStateKind: BillingUiStateKind | undefined;
  pathname: string;
  gatedFrom: unknown;
}

export function resolveTrialGateReturnPath({
  uiStateKind,
  pathname,
  gatedFrom,
}: ResolveTrialGateReturnPathParams): string | undefined {
  if (!pathname.startsWith(APP_PAGES.BILLING)) {
    return undefined;
  }
  if (uiStateKind === undefined) {
    return undefined;
  }
  if (uiStateKind === BillingUiStateKind.APP_TRIAL_EXPIRED) {
    return undefined;
  }
  return isSafeInternalPath(gatedFrom) ? gatedFrom : undefined;
}

export function useTrialGate(): void {
  const navigate = useNavigate();
  const { pathname, search, state } = useLocation();
  const activeTeamId = useActiveTeamId();
  const uiStateKind = useTeamStore((s) => {
    const team = activeTeamId ? s.teamsById[activeTeamId]?.team : undefined;
    return team ? getBillingUiState(team).kind : undefined;
  });

  useEffect(() => {
    if (!activeTeamId) {
      return;
    }
    if (
      shouldRedirectToBilling({
        enabled: IS_BILLING_GATE_ENABLED,
        uiStateKind,
        pathname,
      })
    ) {
      navigate(APP_PAGES.BILLING, {
        replace: true,
        state: {
          gatedFrom: pathname + search,
        } satisfies TrialGateLocationState,
      });
      return;
    }
    const target = resolveTrialGateReturnPath({
      uiStateKind,
      pathname,
      gatedFrom: (state as TrialGateLocationState | null)?.gatedFrom,
    });
    if (target) {
      navigate(target, { replace: true });
    }
  }, [activeTeamId, uiStateKind, pathname, search, state]);
}
